import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  CreditCard,
  Layers,
  Users,
  Settings,
  Info,
  Building2,
  ChevronDown,
  MessageSquare,
  Link2,
  LogOut,
} from 'lucide-react';
import apiClient from '@/api/client';
import { Sparkles } from 'lucide-react';
import { setRedesignEnabled } from '@shared/redesign/flag';
import { useAuthStore } from '@shared/store/authStore';
import { useTabNavigationStore } from '@shared/store/tabNavigationStore';
import { Card, ThemeToggle } from '@shared/components/ui';
import { useCurrentOrganization } from '@shared/hooks/useCurrentOrganization';
import { useCurrentMember } from '@shared/hooks/useCurrentMember';
import { getUserOrganizations, leaveOrganization } from '@shared/api/organizationApi';
import { formatStatus } from '@shared/types/organization';

// -- Helpers ------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getRoleName(role: string): string {
  switch (role) {
    case 'owner':
      return 'Владелец';
    case 'admin':
      return 'Администратор';
    default:
      return role;
  }
}

// -- Menu Row -----------------------------------------------------------------

interface MenuRowProps {
  icon: React.ReactNode;
  label: string;
  subtitle?: string;
  onClick: () => void;
  danger?: boolean;
}

const MenuRow: React.FC<MenuRowProps> = ({ icon, label, subtitle, onClick, danger }) => {
  return (
    <button
      className="w-full flex items-center gap-3 py-3 px-1 text-left active:opacity-60 transition-opacity"
      style={{ borderBottom: '1px solid var(--separator)' }}
      onClick={onClick}
    >
      <div
        className="w-7 h-7 flex items-center justify-center flex-shrink-0"
        style={{ color: danger ? 'var(--danger, #FF3B30)' : 'var(--accent)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <span
          className="text-[17px]"
          style={{ color: danger ? 'var(--danger, #FF3B30)' : 'var(--text-primary)' }}
        >
          {label}
        </span>
      </div>
      {subtitle && (
        <span
          className="text-[15px] flex-shrink-0 mr-1"
          style={{ color: 'var(--text-hint)' }}
        >
          {subtitle}
        </span>
      )}
      <ChevronRight
        size={16}
        className="flex-shrink-0"
        style={{ color: 'var(--text-hint)' }}
      />
    </button>
  );
};

// -- Loading skeleton ---------------------------------------------------------

const Skeleton: React.FC = () => (
  <div className="animate-pulse space-y-4 px-4 pt-6">
    <div className="flex items-center gap-4">
      <div
        className="w-16 h-16 rounded-full"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      />
      <div className="space-y-2">
        <div className="h-5 rounded w-32" style={{ backgroundColor: 'var(--bg-secondary)' }} />
        <div className="h-4 rounded w-20" style={{ backgroundColor: 'var(--bg-secondary)' }} />
      </div>
    </div>
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="h-14 rounded-xl"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      />
    ))}
  </div>
);

// -- Main component -----------------------------------------------------------

export function ProfileScreen() {
  const user = useAuthStore((s) => s.user);
  const selectedOrganizationId = useAuthStore((s) => s.selectedOrganizationId);
  const {
    pushScreen,
    switchTab,
    openOrgSwitcher,
    openDirectionSwitcher,
  } = useTabNavigationStore();

  const queryClient = useQueryClient();
  const { data: organization, isLoading: isLoadingOrg } = useCurrentOrganization();
  const { data: member } = useCurrentMember();

  // Check if user has multiple organizations
  const { data: organizations } = useQuery({
    queryKey: ['user-organizations'],
    queryFn: () => getUserOrganizations(),
    staleTime: 5 * 60 * 1000,
  });

  const hasMultipleOrgs = (organizations?.length ?? 0) > 1;

  // Subscription display
  const subscriptionTier = organization?.subscription_tier ?? 'team';
  const subscriptionStatus = organization?.subscription_status;
  const tierNames: Record<string, string> = {
    free: 'FREE',
    standard: 'STANDARD',
    pro: 'PRO',
    academy: 'ACADEMY',
    basic: 'BASIC',
    premium: 'PREMIUM',
    team: 'TEAM',
    club: 'CLUB',
    enterprise: 'ENTERPRISE',
  };
  const tierDisplay = tierNames[subscriptionTier] || subscriptionTier.toUpperCase();

  if (isLoadingOrg) {
    return <Skeleton />;
  }

  const userName = user?.name || 'Пользователь';
  const userUsername = user?.telegram_username ? `@${user.telegram_username}` : '';

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="px-4 pt-6">
        {/* Organization Switcher (if multiple orgs) */}
        {hasMultipleOrgs && (
          <Card
            onClick={openOrgSwitcher}
            className="mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--accent)', opacity: 0.15 }}
                >
                  <Building2
                    size={20}
                    style={{ color: 'var(--accent)' }}
                  />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-[17px] font-semibold truncate"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {organization?.name || 'Организация'}
                  </div>
                  {member?.role && (
                    <div
                      className="text-[13px]"
                      style={{ color: 'var(--text-hint)' }}
                    >
                      {getRoleName(member.role)}
                    </div>
                  )}
                </div>
              </div>
              <ChevronDown
                size={18}
                style={{ color: 'var(--text-hint)' }}
              />
            </div>
          </Card>
        )}

        {/* User Profile */}
        <div className="flex items-center gap-4 mb-8">
          {/* Avatar */}
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <span className="text-[24px] font-bold text-white">
              {getInitials(userName)}
            </span>
          </div>
          {/* Name + Username */}
          <div className="min-w-0">
            <div
              className="text-[22px] font-bold truncate"
              style={{ color: 'var(--text-primary)' }}
            >
              {userName}
            </div>
            {userUsername && (
              <div
                className="text-[15px]"
                style={{ color: 'var(--text-hint)' }}
              >
                {userUsername}
              </div>
            )}
          </div>
        </div>

        {/* Menu Section 1: Theme + Directions */}
        <Card className="mb-4 !p-0">
          <div className="px-4">
            <ThemeToggle />
          </div>
          <div style={{ borderTop: '0.5px solid var(--separator)' }} />
          <div className="px-3">
            <MenuRow
              icon={<Layers size={20} />}
              label="Мои направления"
              onClick={openDirectionSwitcher}
            />
            <MenuRow
              icon={<Sparkles size={20} />}
              label="Открыть новый дизайн"
              subtitle="Сегодня, спортсмены, группы, оплаты и клуб. Прежняя версия остаётся доступной"
              onClick={() => setRedesignEnabled(true)}
            />
          </div>
        </Card>

        {/* Menu Section 2: Subscription & Organization */}
        <Card className="mb-4">
          {member?.role === 'owner' && (
            <MenuRow
              icon={<CreditCard size={20} />}
              label="Тариф и подписка"
              subtitle={
                subscriptionStatus
                  ? `${tierDisplay} \u00B7 ${formatStatus(subscriptionStatus)}`
                  : tierDisplay
              }
              onClick={() => pushScreen('subscription-manage')}
            />
          )}
          <MenuRow
            icon={<Users size={20} />}
            label="Тренеры"
            onClick={() => {
              switchTab('groups');
              // Push coaches screen inside groups tab
              setTimeout(() => pushScreen('coaches'), 50);
            }}
          />
          {selectedOrganizationId && (
            <MenuRow
              icon={<Settings size={20} />}
              label="Настройки организации"
              subtitle={organization?.name}
              onClick={() =>
                pushScreen('organization-settings', {
                  organizationId: selectedOrganizationId,
                })
              }
            />
          )}
        </Card>

        {/* Leave organization (non-owners only) */}
        {member?.role && member.role !== 'owner' && selectedOrganizationId && (
          <Card className="mb-4">
            <MenuRow
              icon={<LogOut size={20} />}
              label="Выйти из организации"
              danger
              onClick={() => {
                const doLeave = async () => {
                  try {
                    await leaveOrganization(selectedOrganizationId);
                    queryClient.invalidateQueries({ queryKey: ['user-organizations'] });
                    window.location.reload();
                  } catch {
                    window.Telegram?.WebApp?.showAlert?.('Не удалось выйти из организации');
                  }
                };
                if (window.Telegram?.WebApp?.showConfirm) {
                  window.Telegram.WebApp.showConfirm(
                    'Вы уверены, что хотите выйти из организации?',
                    (confirmed: boolean) => { if (confirmed) doLeave(); }
                  );
                } else {
                  if (confirm('Вы уверены, что хотите выйти из организации?')) doLeave();
                }
              }}
            />
          </Card>
        )}

        {/* Web Link */}
        <Card className="mb-4">
          <LinkCodeButton />
        </Card>

        {/* Menu Section 3: About */}
        <Card className="mb-4">
          <MenuRow
            icon={<MessageSquare size={20} />}
            label="Обратная связь"
            onClick={() => {
              try {
                window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
              } catch {}
              pushScreen('feedback');
            }}
          />
          <MenuRow
            icon={<Info size={20} />}
            label="О приложении"
            onClick={() => {
              try {
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
              } catch {}
              alert('Training Assistant Bot v2.0\nTelegram Mini App');
            }}
          />
        </Card>
      </div>
    </div>
  );
}

// -- Link Code Button for Web account linking --

function LinkCodeButton() {
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const generateCode = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.post('/auth/link-code/generate');
      setCode(data.code);
      setCountdown(600);
      // Countdown timer
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCode(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      alert('Не удалось сгенерировать код');
    } finally {
      setLoading(false);
    }
  };

  if (code) {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;

    return (
      <div className="py-3 text-center">
        <p className="text-[13px] mb-2" style={{ color: 'var(--text-hint)' }}>
          Код для привязки Web-аккаунта
        </p>
        <p
          className="text-[32px] font-bold tracking-[0.3em] mb-2"
          style={{ color: 'var(--accent)' }}
        >
          {code}
        </p>
        <p className="text-[12px]" style={{ color: 'var(--text-hint)' }}>
          Действует {minutes}:{seconds.toString().padStart(2, '0')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 py-3 cursor-pointer"
      onClick={generateCode}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: 'var(--accent-light, rgba(245,124,0,0.12))' }}
      >
        <Link2 size={20} style={{ color: 'var(--accent)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-[15px] font-medium" style={{ color: 'var(--text-primary)' }}>
          {loading ? 'Генерация...' : 'Привязать Web-аккаунт'}
        </span>
      </div>
      <ChevronRight size={16} style={{ color: 'var(--text-hint)' }} />
    </div>
  );
}
